/**
 * Overview eklemeleri (katalog s. 13).
 *
 *   · Üstte tek cümle: "September is $22,450 in profit, 18% better than August."
 *     Kodla hesaplanır, yalnız ana para birimindeki kayıtlardan.
 *   · Her satırda "kim yazdı" rozeti: muhasebecinin eklediği ya da düzelttiği
 *     kayıt "✎ Mehmet · Yilmaz Muhasebe corrected". Bilgi veritabanında
 *     (transactions.written_by, imzalı tetikleyici); isim my_accountant()'tan.
 *     Son 30 değişiklikte isim var; daha eskilerde "✎ Your accountant".
 *
 * Katalogdaki "Rent is due in 4 days" tekrarlayan işlem tanımaya bağlı; o özellik
 * yol haritasında ayrı ("sıradaki fikirler"), burada uydurulmaz.
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';

const g = globalThis as any;
interface Row { id: string; date: string; type: string; amount: number; cur?: string; wb?: string | null }

let writers = new Map<string, string>();
let loadedAt = 0;
let loading: Promise<boolean> | null = null;

function el(id: string): HTMLElement | null { return document.getElementById(id); }
function esc(s: unknown): string { return legacy.escapeHtml(String(s ?? '')); }

function money(n: number, cur: string): string {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, currencyDisplay: 'narrowSymbol', maximumFractionDigits: 0 }).format(n); }
  catch { return `${Math.round(n).toLocaleString()} ${cur}`; }
}

/** Ay özeti cümlesi; bu ay kayıt yoksa boş. */
export function summarySentence(rows: Row[], cur: string, now = new Date()): string {
  const key = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  const thisKey = key(now);
  const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const prevKey = key(prevDate);
  let net = 0, prev = 0, seen = false, seenPrev = false;
  for (const r of rows) {
    if (!r || (r.cur || cur) !== cur) continue;
    const k = String(r.date).slice(0, 7);
    const v = r.type === 'income' ? Number(r.amount) || 0 : -(Number(r.amount) || 0);
    if (k === thisKey) { net += v; seen = true; } else if (k === prevKey) { prev += v; seenPrev = true; }
  }
  if (!seen) return '';
  const month = now.toLocaleDateString('en', { month: 'long' });
  const prevMonth = prevDate.toLocaleDateString('en', { month: 'long' });
  /* Metin tamamen koddan (tutar biçimi + ay adı); kullanıcı verisi içermez. */
  let s = `${month} is <b>${money(Math.abs(net), cur)} in ${net >= 0 ? 'profit' : 'loss'}</b>`;
  if (seenPrev && prev !== 0) {
    const pct = Math.round(((net - prev) / Math.abs(prev)) * 100);
    if (pct !== 0) s += `, ${Math.abs(pct)}% ${pct > 0 ? 'better' : 'worse'} than ${prevMonth}`;
  }
  return s + '.';
}

async function loadWriters(): Promise<boolean> {
  if (Date.now() - loadedAt < 60_000) return false;
  if (loading) return loading;
  loading = (async () => {
    const sb = legacy.sb;
    if (!sb) return false;
    let info: any = null;
    try { info = (await sb.rpc('my_accountant'))?.data; } catch { info = null; }
    loadedAt = Date.now();
    const next = new Map<string, string>();
    if (info?.linked) {
      const firm = String(info.firm_name ?? '');
      for (const c of info.changes ?? []) {
        const who = `${String(c.by ?? 'Your accountant').split(/\s+/)[0]} · ${firm}`;
        next.set(String(c.id), c.change === 'added' ? `✎ added by ${who}` : `✎ ${who} corrected`);
      }
    }
    const changed = next.size !== writers.size || [...next].some(([k, v]) => writers.get(k) !== v);
    writers = next;
    return changed;
  })();
  try { return await loading; } finally { loading = null; }
}

function badge(id: unknown, writtenBy: unknown): string {
  const known = writers.get(String(id));
  const me = g._sbUser?.id;
  const text = known ?? (writtenBy && me && writtenBy !== me ? '✎ Your accountant' : '');
  return text ? ` · <span class="ov-wb" style="color:#7aa2ff">${esc(text)}</span>` : '';
}

async function render(): Promise<void> {
  const host = el('hst');
  const u = legacy.user as { exp?: Row[]; setup?: { currency?: string } } | null;
  if (!host || !u) return;
  let line = el('ov-summary');
  if (!line) {
    line = document.createElement('div');
    line.id = 'ov-summary';
    line.style.cssText = 'font-size:15px;line-height:1.5;margin:2px 0 14px;color:var(--txt)';
    host.parentElement?.insertBefore(line, host);
  }
  const s = summarySentence(u.exp ?? [], u.setup?.currency || 'USD');
  line.innerHTML = s;
  line.style.display = s ? '' : 'none';
  if (g.navSide?.() === 'customer' && (await loadWriters())) g.rHL?.();
}

export default defineFeature({
  id: 'overview-extras',
  label: 'Overview özeti ve kim-yazdı rozeti',
  view: null,
  legacyGlobals: {
    ovRender: () => { void render(); },
    ovBadge: (id: unknown, writtenBy: unknown) => badge(id, writtenBy),
  },
});
